import { useEffect, useMemo, useReducer, useState } from "react";
import { createSessionAdminApi } from "@/data/adminApi";
import { fetchAppConfig, type AppConfig } from "@/data/appConfig";
import { configureSupabase } from "@/data/supabaseClient";
import { applyDefaultUrlParams, resolveStartupViewId } from "@/domain/defaultUrlParams";
import { canAccessAdmin, isRespondentOnly, type AuthMode } from "@/domain/permissions";
import { AuthGate } from "@/features/auth/AuthGate";
import { useAuthSession } from "@/features/auth/useAuthSession";
import { I18nProvider } from "@/i18n/I18nProvider";
import { createTranslator } from "@/i18n/translator";
import { useI18n } from "@/i18n/useI18n";
import { AppContext, UiDispatchContext, UiStateContext, type AppContextValue } from "./AppContext";
import { Shell } from "./Shell";
import { DETAILS_LAYOUT, isEditorPanel, uiReducer, type UiState } from "./uiState";
import { useAppData } from "./useAppData";
import { detectInitialLanguage, readUrlState, storeLanguage, writeUrlState } from "./urlState";

function createInitialState(search = window.location.search): UiState {
  const url = readUrlState(search);
  return {
    startupReady: false,
    tab: url.tab ?? "matrix",
    viewId: url.viewId ?? "",
    language: detectInitialLanguage(url.language),
    selectedRegions: new Set(),
    chipSelections: {},
    search: "",
    showTitles: url.showTitles ?? true,
    showIds: url.showIds ?? false,
    listDetailsLevel: 0,
    listSort: { key: "number", direction: "asc" },
    statsFiltersVisible: false,
    detailsPosition: url.detailsPosition ?? DETAILS_LAYOUT.defaultPosition,
    detailsWidth: url.detailsWidth ?? DETAILS_LAYOUT.defaultWidth,
    columnLayoutOverride: url.columnLayout,
    panel: { kind: "none" },
    editOnly: false,
    system: {
      mode: "single",
      period: "all",
      analysisId: "",
      locationId: url.locations[0] ?? "all",
      criterionId: null,
      domainId: null,
      showAbout: false,
      showScale: false,
      expandedDomains: new Set(),
      expandedDescriptions: new Set(),
    },
  };
}

function ConfigurationError() {
  const { t } = useI18n();
  return (
    <p className="database-load-error" role="alert">
      {t("supabaseNotConfigured")}
    </p>
  );
}

export function App() {
  const [ui, dispatch] = useReducer(uiReducer, undefined, createInitialState);
  const [initialQuery] = useState(() => window.location.search);
  const [config, setConfig] = useState<AppConfig | null>(null);
  useEffect(() => {
    void fetchAppConfig().then((next) => {
      if (next.supabase) configureSupabase(next.supabase);
      setConfig(next);
    });
  }, []);
  // Individual mode without Supabase settings stops here with an error.
  const misconfigured = config?.authMode === "individual" && !config.supabase;
  const authMode: AuthMode | null = config && !misconfigured ? config.authMode : null;

  const auth = useAuthSession(authMode === "individual");
  const user = auth.state.status === "signedIn" ? auth.state.user : null;
  const data = useAppData(authMode, user);
  const { settings } = data;

  // Wait for the actual settings before choosing a view. Otherwise the early
  // first-visible-view fallback can write view=B to the address bar, turning
  // the absent URL parameter into an apparent explicit override of view=F.
  useEffect(() => {
    if (
      authMode !== "individual" ||
      !user ||
      ui.startupReady ||
      data.status.state !== "ready"
    ) {
      return;
    }
    const mergedSearch = applyDefaultUrlParams(initialQuery, settings.defaultUrlParams);
    const merged = readUrlState(mergedSearch);
    const url = new URL(window.location.href);
    url.search = mergedSearch;
    if (url.href !== window.location.href) {
      window.history.replaceState(window.history.state, "", url);
    }
    dispatch({
      type: "initializeStartup",
      values: {
        tab: merged.tab ?? ui.tab,
        viewId: resolveStartupViewId(merged.viewId, settings.views),
        language:
          merged.language && settings.languages.some((entry) => entry.code === merged.language)
            ? merged.language
            : settings.languages.some((entry) => entry.code === ui.language)
              ? ui.language
              : (settings.languages.find((entry) => entry.code === "en")?.code ??
                settings.languages[0]?.code ??
                "no"),
        showIds: merged.showIds ?? ui.showIds,
        showTitles: merged.showTitles ?? ui.showTitles,
        detailsPosition: merged.detailsPosition ?? ui.detailsPosition,
        detailsWidth: merged.detailsWidth ?? ui.detailsWidth,
        systemLocationId: merged.locations[0] ?? ui.system.locationId,
      },
    });
  }, [
    authMode,
    user,
    ui,
    data.status.state,
    initialQuery,
    settings.defaultUrlParams,
    settings.languages,
    settings.views,
  ]);

  // Shared mode only offers the built-in languages.
  useEffect(() => {
    if (!authMode || (authMode === "individual" && !ui.startupReady)) return;
    if (!settings.languages.some((language) => language.code === ui.language)) {
      dispatch({
        type: "setLanguage",
        language: settings.languages.some((l) => l.code === "en") ? "en" : "no",
      });
    }
  }, [authMode, settings.languages, ui.language, ui.startupReady]);

  // Fall back to the first visible view when the URL names an unknown one.
  useEffect(() => {
    if (!authMode || (authMode === "individual" && !ui.startupReady)) return;
    const valid = settings.views.filter((view) => view.visible);
    if (valid.length && !valid.some((view) => view.id === ui.viewId)) {
      dispatch({ type: "setView", viewId: valid[0]!.id });
    }
  }, [authMode, settings.views, ui.viewId, ui.startupReady]);

  // Respondents only have the intervention list; the matrix tab hosts their
  // editor, so a stored `page=matrix` without an open editor falls back too.
  useEffect(() => {
    if (
      isRespondentOnly(user) &&
      ui.tab !== "list" &&
      !(ui.tab === "matrix" && isEditorPanel(ui.panel))
    )
      dispatch({ type: "setTab", tab: "list" });
  }, [user, ui.tab, ui.panel]);

  useEffect(() => {
    // Configuration/auth mode is asynchronous. In particular, do not write
    // temporary React fallback values to the URL before we know the mode:
    // that would turn an absent parameter into a spurious explicit override.
    if (!authMode || !ui.viewId || (authMode === "individual" && !ui.startupReady)) return;
    writeUrlState(ui);
    storeLanguage(ui.language);
  }, [ui, authMode]);

  const sessionExpiredText = createTranslator({
    language: ui.language,
    dictionary: settings.dictionary,
    arenas: null,
  }).t("authSessionExpired");
  const adminApi = useMemo(
    () =>
      authMode === "individual" && canAccessAdmin(user)
        ? createSessionAdminApi(() => sessionExpiredText)
        : null,
    [authMode, user, sessionExpiredText],
  );

  const appContext = useMemo<AppContextValue | null>(
    () =>
      authMode
        ? {
            authMode,
            user,
            data,
            adminApi,
            signOut: () => {
              if (authMode === "individual") void auth.signOut();
              else
                window.location.href = `/auth/logout?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`;
            },
          }
        : null,
    [authMode, user, data, adminApi, auth],
  );

  const needsLogin =
    authMode === "individual" &&
    auth.state.status !== "signedIn" &&
    auth.state.status !== "loading";

  return (
    <I18nProvider language={ui.language} dictionary={settings.dictionary} arenas={settings.arenas}>
      <UiStateContext.Provider value={ui}>
        <UiDispatchContext.Provider value={dispatch}>
          {misconfigured ? (
            <ConfigurationError />
          ) : needsLogin ? (
            <AuthGate auth={auth} />
          ) : appContext && (authMode === "shared" || user) ? (
            <AppContext.Provider value={appContext}>
              <Shell />
            </AppContext.Provider>
          ) : null}
        </UiDispatchContext.Provider>
      </UiStateContext.Provider>
    </I18nProvider>
  );
}
